import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Divider, Grid, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Popper } from "Components/_Shared/Popper";
import { type MovableContractFormType, getMovableContractFormSchema } from "Forms/Schemas/Assets/movableContractFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getListMovableAssetContractsQueryKey,
  useCreateOtherMovableAssetContract,
  useDeleteAssetContract,
  useGetOtherMovableAssetContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateOtherMovableAssetContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  ListAnalyzerPartnersToolType,
  LoadValuesOptionExpenseType,
  type ProposedSolutionStep,
} from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";
import type { SubmitContractFormHandle } from "./ContractDrawer";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly onClose: () => void;
  readonly onPendingChange?: (pending: boolean) => void;
};

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: ${colors.backgroundSecondary};
  }
`;

export const MovableContractOtherForm = forwardRef<SubmitContractFormHandle, Props>(
  ({ assetItemId, assetContractId, onClose, onPendingChange }, ref) => {
    const queryClient = useQueryClient();
    const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
    const { t } = useResource();

    const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

    const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });
    const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);
    const { data: movableContract } = useGetOtherMovableAssetContract(analysisId, versionId, assetItemId, assetContractId ?? "", {
      query: { enabled: !!assetContractId },
    });

    const { mutateAsync: createAsync, isPending: isCreating } = useCreateOtherMovableAssetContract();
    const { mutateAsync: editAsync, isPending: isEditing } = useUpdateOtherMovableAssetContract();
    const { mutateAsync: deleteAsync, isPending: isDeleting } = useDeleteAssetContract();

    const isMutating = isCreating || isEditing || isDeleting;

    useEffect(() => {
      onPendingChange?.(isMutating);
    }, [isMutating, onPendingChange]);

    const defaultValues = useMemo(
      () => ({
        frequency: movableContract?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        newFrequency: movableContract?.proposedSolution?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        proposedSolutionStepType: movableContract?.proposedSolution?.step ?? "keep",
        partnerId: movableContract?.partnerId?.toString(),
        newPartnerId: movableContract?.proposedSolution?.partnerId?.toString() ?? undefined,
        contractAnniversaryDate: movableContract?.contractAnniversaryDate ?? undefined,
        insuranceAmount: movableContract?.insuranceAmount?.value.toString() ?? ("0" as any),
        newInsuranceAmount: movableContract?.proposedSolution?.insuranceAmount?.value.toString() ?? ("0" as any),
        note: movableContract?.note ?? undefined,
      }),
      [movableContract],
    );

    const methods = useForm<MovableContractFormType>({
      resolver: yupResolver(getMovableContractFormSchema(t)),
      mode: "onBlur",
      defaultValues,
    });

    const { control, handleSubmit, reset, setValue, watch } = methods;

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (!!assetContractId) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      delete: onDelete,
    }));

    const frequencies: Option[] = [
      { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
      { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
      { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
      { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
      { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
    ];

    const proposedSolutionStepTypes: Option[] = [
      { value: "keep", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Keep) },
      { value: "create_recommended", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.CreateRecommended) },
      { value: "modify", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Modify) },
    ];

    const insurancePartners: Option[] =
      insurancePartnersData?.map((partner) => ({ value: partner.id.toString(), name: partner.name })) || [];

    const filteredExpenseOptions = expenseOptions?.filter((option) => option.expenseType === LoadValuesOptionExpenseType.other) ?? [];

    const buildAmount = (value: number | undefined, frequency: string) => ({
      value: value || 0,
      currencyCode: "CZK",
      frequency: frequency as AmountFrequency,
    });

    const buildBody = (data: MovableContractFormType) => ({
      partnerId: Number(data.partnerId),
      contractAnniversaryDate: data.contractAnniversaryDate || undefined,
      insuranceAmount: buildAmount(data.insuranceAmount, data.frequency),
      proposedSolution: {
        step: data.proposedSolutionStepType as ProposedSolutionStep,
        partnerId: data.newPartnerId ? Number(data.newPartnerId) : undefined,
        insuranceAmount: buildAmount(data.newInsuranceAmount, data.newFrequency),
      },
      note: data.note || "",
    });

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: getListMovableAssetContractsQueryKey(analysisId, versionId, assetItemId) });
    };

    const onCreate = async (data: MovableContractFormType) => {
      try {
        await createAsync({ analysisId, versionId, itemId: assetItemId, data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractCreateSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onEdit = async (data: MovableContractFormType) => {
      try {
        await editAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "", data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractEditSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onDelete = async () => {
      try {
        await deleteAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "" });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractDeleteSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    useEffect(() => {
      if (movableContract) {
        reset(defaultValues);
      }
    }, [movableContract, reset, defaultValues]);

    const partnerId = watch("partnerId");
    const insuranceAmount = watch("insuranceAmount");
    const frequency = watch("frequency");
    const proposedSolutionStepType = watch("proposedSolutionStepType");

    return (
      <FormProvider {...methods}>
        <form>
          <Box>
            <Stack spacing={Spacing.XXL}>
              <Grid container spacing={Spacing.XL}>
                {filteredExpenseOptions.length > 0 && (
                  <Grid size={6}>
                    <Popper
                      placement="bottom-start"
                      isOpen={expenseOptionsDropdownOpen}
                      onOpen={() => {
                        setExpenseOptionsDropdownOpen(true);
                      }}
                      btnEl={
                        <Button
                          variant="text"
                          disabled={readOnly}
                          sx={{ width: "fit-content" }}
                          endIcon={<ChevronDownIcon sx={{ color: "primary.main" }} />}
                        >
                          {t(Resources.AnalysisDetail.AssetsAndContracts.Form.LoadFromExistingExpenses)}
                        </Button>
                      }
                    >
                      <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                        {filteredExpenseOptions.map((option, index) => (
                          <StyledItem
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingX: Spacing.M,
                              paddingY: Spacing.S,
                              borderRadius: Radius.S,
                            }}
                            key={`${option.expenseType}-${index}`}
                            onClick={() => {
                              setValue("insuranceAmount", option.amount?.value.toString() as any);
                              if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                              setExpenseOptionsDropdownOpen(false);
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.Other)}
                              </Typography>
                              <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                                {option.institution}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="secondary" fontWeight={500}>
                              {formatCurrency(option.amount?.value, option.amount?.currencyCode)}/
                              {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                            </Typography>
                          </StyledItem>
                        ))}
                      </Stack>
                    </Popper>
                  </Grid>
                )}
                <Grid size={8}>
                  <FormSelect
                    control={control}
                    name="partnerId"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
                    required
                    options={toSelectOptions(insurancePartners)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormDatePicker
                    control={control}
                    name="contractAnniversaryDate"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractAnniversaryDate)}
                    disabled={readOnly}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="insuranceAmount"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormSelect
                    control={control}
                    name="frequency"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                    options={toSelectOptions(frequencies)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography fontWeight={600} variant="subtitle1">
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolutionSteps)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <FormSelect
                    control={control}
                    name="proposedSolutionStepType"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolution)}
                    options={toSelectOptions(proposedSolutionStepTypes)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    onChange={(value) => {
                      if (value === "modify") {
                        if (partnerId) setValue("newPartnerId", partnerId);
                        if (insuranceAmount) setValue("newInsuranceAmount", insuranceAmount);
                        if (frequency) setValue("newFrequency", frequency);
                      }
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              {proposedSolutionStepType !== "keep" && (
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <FormSelect
                      control={control}
                      name="newPartnerId"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NewPartner)}
                      required
                      options={toSelectOptions(insurancePartners)}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="newInsuranceAmount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                      required
                      valueType="text"
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormSelect
                      control={control}
                      name="newFrequency"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                      required
                      options={toSelectOptions(frequencies)}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.Common.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Stack>
          </Box>
        </form>
      </FormProvider>
    );
  },
);
